import React, { useState, useEffect } from 'react';
import { formatDateForInput } from '../../utils/dateUtils';
import { useUserContext } from '../../store/user_context';
import './Settings.css';

const EmployeeOrdersDetail = () => {
    const { user } = useUserContext();
    const [ordersData, setOrdersData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [building, setBuilding] = useState(0);
    const [menuId, setMenuId] = useState('all');
    const [availableMenus, setAvailableMenus] = useState([]);
    const [groupedData, setGroupedData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});

    const apiUrl = import.meta.env.VITE_API_URL;
    const usersOrdersByTodayEndpoint = import.meta.env.VITE_get_users_orders_by_today;
    const getTodayMenuEndpoint = import.meta.env.VITE_get_today_menu;
    
    useEffect(() => {
        // Fetch available menus when user is available
        if (user?.userId) {
            fetchAvailableMenus();
        }
    }, [user?.userId]);

    const fetchAvailableMenus = async () => {
        if (!user?.userId) return;
        
        try {
            const response = await fetch(`${apiUrl}${getTodayMenuEndpoint}?userId=${user.userId}`);
            if (response.ok) {
                const data = await response.json();
                // Convert object to array and extract menus for dropdown
                const menusArray = data.menus || [];
                const menus = menusArray.map(menu => ({
                    id: menu.menuID, // camelCase: menuId
                    name: menu.menuName // camelCase: menuName
                }));
                setAvailableMenus(menus);
            }
        } catch (error) {
            console.error('Error fetching menus:', error);
        }
    };

    const fetchOrdersData = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (building) params.append('building', building);
            
            // Handle menuId parameter
            if (menuId === 'all') {
                // Send all menu IDs as comma-separated string
                const allMenuIds = availableMenus.map(menu => menu.id).join(',');
                if (allMenuIds) {
                    params.append('menuId', allMenuIds);
                }
            } else if (menuId && !isNaN(parseInt(menuId))) {
                // Send specific menu ID as string
                params.append('menuId', menuId.toString());
            }
            
            const response = await fetch(`${apiUrl}${usersOrdersByTodayEndpoint}?${params}`);

            if (response.ok) {
                const data = await response.json();
                setOrdersData(data);
                groupOrdersByEmployee(data);
            } else {
                console.error('Error fetching orders');
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const groupOrdersByEmployee = (data) => {
        // Group data by employee
        const grouped = {};
        
        data.forEach(order => {
            const employeeName = order.name;
            if (!grouped[employeeName]) {
                grouped[employeeName] = {
                    name: employeeName,
                    orderCode: order.orderCode,
                    orders: [],
                    totalPerMenu: order.totalPerMenu || 0
                };
            }
            
            grouped[employeeName].orders.push(order);
        });

        // Convert to array format for easier rendering
        const groupedArray = [{
            employees: Object.values(grouped)
        }];

        setGroupedData(groupedArray);
    };

    const handleSearch = () => {
        // Clear previous validation errors
        setValidationErrors({});
        
        // No validation needed - user can select "All Restaurants" or specific restaurant
        fetchOrdersData();
    };


    const printDiv = () => {
        if (groupedData.length === 0) {
            alert('من فضلك قم بالبحث اولا');
            return;
        }

        const currentDate = formatDateForInput(new Date());
        const printWindow = window.open('', '_blank', 'width=900,height=700');
        if (!printWindow) return;

        let printContent = `
            <!DOCTYPE html>
            <html lang="ar" dir="rtl">
            <head>
                <meta charset="utf-8">
                <title>عرض إجمالي الطلبات لكل موظف</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; direction: rtl; }
                    h1, h2, h3 { text-align: center; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
                    th { background-color: #f5f5f5; font-weight: bold; }
                    .nested-table { margin: 0; }
                    .nested-table th, .nested-table td { border: 1px solid #ccc; padding: 4px; font-size: 12px; }
                    @media print {
                        body { margin: 0; }
                        .page-break { page-break-before: always; }
                        @page { 
                            size: A4 landscape; 
                            margin: 10mm; 
                        }
                    }
                </style>
            </head>
            <body>
                <h1>عرض إجمالي الطلبات لكل موظف - ${currentDate}</h1>
        `;

        groupedData.forEach((floor, floorIndex) => {
            if (floorIndex > 0) printContent += '<div class="page-break"></div>';
            
            printContent += `
                <h2>الطلبات</h2>
                <table>
                    <thead>
                        <tr>
                            <th>الموظف</th>
                            <th>رقم الطلب</th>
                            <th>الطلب</th>
                            <th>الإجمالي</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            floor.employees.forEach(employee => {
                printContent += `
                    <tr>
                        <td>${employee.name}</td>
                        <td>${employee.orderCode}</td>
                        <td>
                            <table class="nested-table">
                                <thead>
                                    <tr>
                                        <th>الصنف</th>
                                        <th>النوع</th>
                                        <th>العدد</th>
                                        <th>السعر</th>
                                        <th>المطعم</th>
                                    </tr>
                                </thead>
                                <tbody>
                `;

                employee.orders.forEach(order => {
                    printContent += `
                        <tr>
                            <td>${order.itemName || ''}</td>
                            <td>${order.categoryName || ''}</td>
                            <td>${order.quantity || ''}</td>
                            <td>${order.price || ''}</td>
                            <td>${order.menuName || ''}</td>
                        </tr>
                    `;
                });

                printContent += `
                                </tbody>
                            </table>
                        </td>
                        <td>${employee.totalPerMenu}</td>
                    </tr>
                `;
            });

            printContent += `
                    </tbody>
                </table>
            `;
        });

        printContent += `
                <script>
                    window.onload = function() { 
                        window.focus(); 
                        window.print(); 
                        window.close(); 
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    return (
        <section className="main_content">
            <div className="container">
                <h1 className="page-title">عرض إجمالي الطلبات لكل موظف</h1>

                {/* Print Button */}
                {groupedData.length > 0 && (
                    <div className="sheet_actions">
                        <button 
                            type="button" 
                            className="btn_icon" 
                            onClick={printDiv}
                            title="طباعة"
                        >
                            <i className="mIcon">&#xf1b0;</i>
                        </button>
                    </div>
                )}

                {/* Filters */}
                <form className="form-horizontal" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                    <div className="form-group">
                        <label>المطعم:</label>
                        <div className="field">
                            <select 
                                value={menuId} 
                                onChange={(e) => {
                                    setMenuId(e.target.value);
                                    // Clear validation error when user selects a menu
                                    if (validationErrors.menuId) {
                                        setValidationErrors(prev => ({
                                            ...prev,
                                            menuId: ''
                                        }));
                                    }
                                }}
                                className="form-control"
                            >
                                <option value="all">كل المطاعم</option>
                                {availableMenus.map(menu => (
                                    <option key={menu.id} value={menu.id}>
                                        {menu.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <button type="submit" className="btn btn-primary">
                        بحث
                    </button>
                </form>

                {/* Orders Display */}
                <div id="areaToPrint" className="employees_orders">
                    {loading ? (
                        <div className="loading-message">جاري التحميل...</div>
                    ) : groupedData.length > 0 ? (
                        groupedData.map((group, groupIndex) => (
                            <div key={groupIndex} className="floor-section">
                                <h2 id="title">
                                    الطلبات
                                </h2>

                                <table className="table table-bordered table-curved">
                                    <thead className="thead-default">
                                        <tr>
                                            <th>الموظف</th>
                                            <th>رقم الطلب</th>
                                            <th>الطلب</th>
                                            <th>الإجمالي</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.employees
                                            .sort((a, b) => a.name.localeCompare(b.name, 'ar'))
                                            .map((employee, empIndex) => (
                                            <tr key={empIndex}>
                                                <td>{employee.name}</td>
                                                <td>
                                                    <bdo dir="ltr">{employee.orderCode}</bdo>
                                                </td>
                                                <td>
                                                    <table className="table table-bordered table-curved" id="childTbl">
                                                        <thead className="thead-inverse">
                                                            <tr>
                                                                <th>الصنف</th>
                                                                <th>النوع</th>
                                                                <th>العدد</th>
                                                                <th>السعر</th>
                                                                <th>المطعم</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {employee.orders.map((order, orderIndex) => (
                                                                <tr key={orderIndex}>
                                                                    <td>{order.itemName}</td>
                                                                    <td>{order.categoryName}</td>
                                                                    <td>
                                                                        <bdo dir="ltr">{order.quantity}</bdo>
                                                                    </td>
                                                                    <td>
                                                                        <bdo dir="ltr">{order.totalPrice}</bdo>
                                                                    </td>
                                                                    <td>{order.menuName}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                                <td>
                                                    <bdo dir="ltr">{employee.totalPerMenu}</bdo>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))
                    ) : (
                        <div className="no-data">لا توجد بيانات للعرض</div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default EmployeeOrdersDetail;
